import type { PaaRow } from "../types";

// The review sequence: which sheets need her eye, in what order, and how far
// through she is.
//
// WHY THIS EXISTS. She has to look at every page — that was never the problem
// and nothing here reduces it. The problem is that the app has no idea what she
// has already looked at, so the only way to be sure is to scan the whole table
// again. Sixty rows of which nine changed is a list nobody reads to the end
// twice.
//
// Kept out of the component so the sequence can be tested without rendering
// anything, and so the count on screen and the order of `Next` come from the
// same array — two derivations of "what is left" would eventually disagree, and
// the one she is looking at is the true one.

/** A sheet worth stepping through. Rows with no sheet number are excluded: there
 *  is nothing to compare and nothing to describe until she names it, and a queue
 *  that opens on a blank row teaches her the queue is broken. */
export function isReviewable(row: PaaRow): boolean {
  return row.sheetId.trim().length > 0;
}

/** The queue, in the table's own order.
 *
 *  Deliberately NOT sorted by urgency. The order on screen is the order of the
 *  filing, and a queue that jumps around loses the one piece of context a plan
 *  set gives you for free — the sheet before and the sheet after. */
export function reviewQueue(rows: readonly PaaRow[]): PaaRow[] {
  return rows.filter(isReviewable);
}

export interface ReviewProgress {
  /** Sheets she has confirmed. */
  reviewed: number;
  /** Sheets in the queue. */
  total: number;
  /** Still to look at. */
  remaining: number;
  /** Of the remaining, the ones the engine could not settle on its own. */
  needsAttention: number;
  done: boolean;
}

export function reviewProgress(rows: readonly PaaRow[]): ReviewProgress {
  const queue = reviewQueue(rows);
  const reviewed = queue.filter((r) => r.reviewed).length;
  const outstanding = queue.filter((r) => !r.reviewed);
  return {
    reviewed,
    total: queue.length,
    remaining: outstanding.length,
    needsAttention: outstanding.filter(needsAttention).length,
    // An empty filing is not "done" — there is nothing to be done with.
    done: queue.length > 0 && reviewed === queue.length,
  };
}

/** The engine says it could not finish this row on its own. */
export function needsAttention(row: PaaRow): boolean {
  return !!row.needsReview || !!row.revisionLooksStale || !!row.oldSheetMissing;
}

export function indexOfRow(rows: readonly PaaRow[], id: string | null): number {
  if (!id) return -1;
  return reviewQueue(rows).findIndex((r) => r.id === id);
}

/** Step by one within the queue, without wrapping.
 *
 *  No wrap on purpose: arriving back at sheet 1 after the last sheet reads as a
 *  bug, and it destroys the only cue that the pass is over. Returns null at
 *  either end, and the caller decides what that means. */
export function step(
  rows: readonly PaaRow[],
  fromId: string | null,
  delta: number,
): PaaRow | null {
  const queue = reviewQueue(rows);
  if (queue.length === 0) return null;

  const at = indexOfRow(rows, fromId);
  // Nothing focused yet: `Next` starts at the top rather than at index 1.
  const target = at < 0 ? (delta > 0 ? 0 : queue.length - 1) : at + delta;
  return queue[target] ?? null;
}

/** The next sheet she has not confirmed, at or after `fromId`, else the first
 *  outstanding one anywhere. This is what `Confirm` advances to.
 *
 *  Looking forward first and only then wrapping to the earliest outstanding
 *  sheet is what makes the pass finishable: the queue drains rather than
 *  bouncing between two sheets she keeps skipping. */
export function nextOutstanding(
  rows: readonly PaaRow[],
  fromId: string | null,
): PaaRow | null {
  const queue = reviewQueue(rows);
  const at = indexOfRow(rows, fromId);

  for (let i = at + 1; i < queue.length; i++) {
    if (!queue[i].reviewed) return queue[i];
  }
  for (let i = 0; i <= at && i < queue.length; i++) {
    if (!queue[i].reviewed) return queue[i];
  }
  return null;
}

/** Mark one sheet confirmed, or un-confirm it. */
export function setReviewed(
  rows: readonly PaaRow[],
  id: string,
  reviewed: boolean,
): PaaRow[] {
  return rows.map((r) => (r.id === id ? { ...r, reviewed } : r));
}

/**
 * Drop the confirmation on a row whose note is about to change.
 *
 * Called from the `row` SSE merge. What she confirmed was a specific sentence;
 * once the engine rewrites it she has not seen the new one, and a tick that
 * outlives the thing it ticked is worse than no tick at all — it is the same
 * failure as a green test that asserts nothing.
 *
 * An identical note is not a change, so a re-compare that lands on the same
 * sentence keeps her confirmation. That matters: re-running a comparison over a
 * whole filing must not silently wipe an afternoon of review.
 */
export function reviewedAfterNote(
  row: PaaRow,
  incomingNote: string | null | undefined,
): boolean {
  if (!row.reviewed) return false;
  return (row.changeNote ?? "") === (incomingNote ?? "");
}
